import { Select } from "@dmm-com/rocket-web-ui-library";
import type { SelectOption } from "@dmm-com/rocket-web-ui-library/es6/components/select";
import { memo, useEffect, useMemo, useState } from "react";
import { DmmInfoTag } from "./dmm-info-tag";
import { PlaybackCheckerTag } from "./playback-checker-tag";
import { ProductInfoTag } from "./product-info-tag";
import * as Styled from "./style";
import { ViewConditionTag } from "./view-condition-tag";
import { useRouter } from "next/router";
import { isVideoLegacySeason, isVideoSeason } from "~/components/blocks/product/type-guard";
import type { VodDetailModalHref } from "~/const/hrefs";
import { useMatchMedia } from "~/hooks/use-match-media";
import { useQueryString } from "~/hooks/use-query-string";
import type { G_GetVideoQuery } from "~/lib/graphql";
import { G_VideoContentType, G_VideoSeasonType } from "~/lib/graphql";
import { generateEpisodeNumberTitle } from "~/util/generate-episode-number-title";

type TagTitleProps = {
  videoData: G_GetVideoQuery["video"];
};

const SELECT_MIN_WIDTH = {
  PC: 320,
  SP: 230,
} as const;

/**
 * 作品のタイトルとタグを表示するコンポーネント
 * @param props {@link TagTitleProps}
 */
export const TagTitle = memo((props: TagTitleProps) => {
  const { videoData } = props;

  const { season, content } = useQueryString();
  const { push, pathname, query } = useRouter();
  const { isSpSize } = useMatchMedia();

  const selectOptions: SelectOption[] = useMemo(() => {
    if (isVideoSeason(videoData)) {
      return videoData.relatedSeasons.map((item) => {
        return {
          listValue: item.id,
          optionLabelElement: item.title,
        };
      });
    }

    return [];
  }, [videoData]);

  const [selectedValue, setSelectedValue] = useState("");

  const handleOptionClick = async (selectedValue: string) => {
    setSelectedValue(selectedValue);
    void push({ pathname, query: { ...query, season: selectedValue } } as VodDetailModalHref);
  };

  const detailHeadingTitle = useMemo(() => {
    const { titleName, seasonType } = videoData;

    /** VideoSeason（単話または複数話） | クエリパラメータに ?content が付与された場合に`true`になる */
    const isEpisodeDetail = isVideoSeason(videoData) && content !== undefined;

    if (!isEpisodeDetail) return titleName;

    // エピソード末端 かつ 単話の場合は titleName だけを表示する
    if (seasonType === G_VideoSeasonType.SingleEpisode) return titleName;

    // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
    if (!videoData.episode) return "";

    const {
      episode: { episodeNumberName, episodeTitle },
    } = videoData;

    return generateEpisodeNumberTitle({ episodeNumberName, episodeTitle });
  }, [season, content, videoData]);

  const isDisplaySelectSeasonType = useMemo(() => {
    return [G_VideoSeasonType.MultiEpisodes, G_VideoSeasonType.SingleEpisode].includes(videoData.seasonType);
  }, [videoData.seasonType]);

  const isVrContent = useMemo(() => {
    if (isVideoLegacySeason(videoData)) {
      return (
        videoData.content.contentType === G_VideoContentType.LiveVr ||
        videoData.content.contentType === G_VideoContentType.VodVr
      );
    }

    return false;
  }, [videoData]);

  useEffect(() => {
    if (!isDisplaySelectSeasonType || selectOptions.length === 0 || content !== undefined) return;

    const initialSelectedOptions = selectOptions.find((item) => item.listValue === season);

    if (initialSelectedOptions === undefined) return;

    setSelectedValue(initialSelectedOptions["listValue"]);
  }, [selectedValue, videoData, content]);

  return (
    <div>
      <DmmInfoTag videoData={videoData} />

      <Styled.DivTopGrid>
        {/** MEMO : クエリパラメータに`?content`が付与される かつ seasonType が 複数話 の場合のみ表示する */}
        {content !== undefined && videoData.seasonType === G_VideoSeasonType.MultiEpisodes && (
          <div>
            <Styled.SpanTitleName>{videoData.titleName}</Styled.SpanTitleName>

            {videoData.titleName !== videoData.seasonName && (
              <Styled.SpanSeasonName>{videoData.seasonName}</Styled.SpanSeasonName>
            )}
          </div>
        )}

        <Styled.Heading>{detailHeadingTitle}</Styled.Heading>

        {/** MEMO（kimura）: シーズン情報が2つ以上ある場合は、シーズン選択のSelectを描画する */}
        {/** MEMO（kimura）: 舞台・グラビアの場合は、一律でシーズン選択のSelectは描画しない */}
        {/** MEMO（kimura）: エピソード末端の場合は、シーズン選択プルダウンは表示されない */}
        {isDisplaySelectSeasonType && selectOptions.length > 1 && content === undefined && (
          <Select
            minWidth={`${isSpSize ? SELECT_MIN_WIDTH.SP : SELECT_MIN_WIDTH.PC}px`}
            selectedValue={selectedValue}
            options={selectOptions}
            onOptionClick={handleOptionClick}
          />
        )}
      </Styled.DivTopGrid>

      <Styled.DivBottomGrid>
        <ProductInfoTag videoData={videoData} />

        <ViewConditionTag videoData={videoData} />

        {isVrContent && <Styled.ParVrText>VR対応アプリでのみ視聴できます</Styled.ParVrText>}

        <PlaybackCheckerTag videoData={videoData} />
      </Styled.DivBottomGrid>
    </div>
  );
});

TagTitle.displayName = "TagTitle";
